import { Inject, Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscriber } from 'rxjs';

import '@helpers/dayjs.setup';

import { CenterInfo } from '@models/center/info/center-info';
import { WebPageDomainType } from '@enumerates/web-page/webpage-domain-type';
import { CookiebotHelper } from '@helpers/cookiebot-helper';
import { GoogleTagHelper } from '@helpers/google-tag-helper';

import { CenterService } from '@services/center/center.service';
import { Domain } from '@models/webpage/domain/domain';
import { PaymentsDomainHelper } from '@helpers/payments-domain-helper';
import { AppLoadedService } from '@services/app-loaded/app-loaded.service';
import { MetricoolHelper } from '@helpers/metricool-helper';
import { WebPage } from '@models/webpage/webpage';
import { SessionService } from '../session.service';
import { InternationalizationService } from '../internationalization/internationalization.service';

import { CacheService } from '../cache/cache.service';
import { ScriptService } from '../script/script.service';
import { WINDOW } from '../window/window.provider';

@Injectable({
    providedIn: 'root'
})
export class PublicService {
    private readonly DEFAULT_HOUR_FORMAT_24 = 'HH:mm';
    private readonly DEFAULT_HOUR_FORMAT_12 = 'h:mm a';

    constructor(
        @Inject(WINDOW) private readonly window: Window,
        private readonly centerService: CenterService,
        private readonly sessionService: SessionService,
        private readonly internationalizationService: InternationalizationService,
        private readonly translateService: TranslateService,
        private readonly cacheService: CacheService,
        private readonly scriptService: ScriptService,
        private readonly router: Router,
        private readonly appLoadedService: AppLoadedService,
        private readonly titleService: Title
    ) {}

    checkIfCanActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        this.sessionService.setViewMode(route);
        let domain = this.sessionService.getDomain();
        if (domain === 't') {
            domain = '';
        }
        if (!this.sessionService.getIsCustomDomain()) {
            if (domain == null || domain === '') {
                if (route.params.domain) {
                    this.sessionService.setDomain(route.params.domain);
                }
            }
        }

        if (route.params.domain) {
            domain = route.params.domain;
        }

        return this.checkDomain(domain);
    }

    checkDomain(domain: string): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            if (domain === null || domain === '') {
                domain = this.window.location.hostname;
                this.sessionService.setIsCustomDomain(true);
            }

            const centerInfo = this.sessionService.getCenterInfo();
            if (centerInfo != null) {
                this.titleService.setTitle(centerInfo.name);
            }
            const webPageKey = `webpage-${domain}`;

            const loadedScriptsKey = `loadedScripts-${domain}`;
            const loadedScriptMetricoolKey = `loadedScriptMetricool-${domain}`;
            const loadedScriptsGoogleKey = `loadedScriptsGoogle-${domain}`;
            const centerInfoKey = `center-info-${domain}`;
            if (centerInfo === null || centerInfo.id === 0 || centerInfo.domain.url?.toUpperCase() !== domain.toUpperCase()) {
                this.cacheService.get(webPageKey, this.centerService.getWebPage(domain), 3600).subscribe({
                    next: (data) => {
                        if (data) {
                            this.cacheService.get(centerInfoKey, this.centerService.info(domain), 3600).subscribe({
                                next: (dataInfo: unknown) => this.handleInfoAndReload(data, domain, loadedScriptsKey, loadedScriptMetricoolKey, loadedScriptsGoogleKey, centerInfo, observable, dataInfo),
                                error: () => {
                                    this.router.navigateByUrl('unauthorized');
                                    observable.next(false);
                                }
                            });
                        } else {
                            this.router.navigateByUrl('unauthorized');
                            observable.next(false);
                        }
                    },
                    error: () => {
                        this.router.navigateByUrl('unauthorized');
                        observable.next(false);
                    }
                });
            } else if (PaymentsDomainHelper.isDomainMaster(domain)) {
                document.documentElement.style.setProperty('--show-translations', 'none');
                observable.next(true);
            } else {
                this.cacheService.get(centerInfoKey, this.centerService.info(domain), 3600).subscribe({
                    next: (dataInfo: any) => {
                        if (dataInfo) {
                            if (dataInfo.showMeridian) {
                                dataInfo.hourFormat = this.DEFAULT_HOUR_FORMAT_12;
                            } else {
                                dataInfo.hourFormat = this.DEFAULT_HOUR_FORMAT_24;
                            }

                            const centerInfoCurrent = new CenterInfo().build(dataInfo);
                            centerInfoCurrent.domain = centerInfo.domain;
                            if (centerInfo.paymentConfiguration?.canBook !== undefined) {
                                centerInfoCurrent.paymentConfiguration.canBook = centerInfo.paymentConfiguration.canBook;
                            }
                            this.sessionService.setCenterInfo(centerInfoCurrent);

                            this.handleExistingCenterInfoWithWebPage(domain, webPageKey, loadedScriptsKey, loadedScriptMetricoolKey, loadedScriptsGoogleKey, centerInfoCurrent, observable);
                        } else {
                            this.handleExistingCenterInfoWithWebPage(domain, webPageKey, loadedScriptsKey, loadedScriptMetricoolKey, loadedScriptsGoogleKey, centerInfo, observable);
                        }
                    },
                    error: () => this.handleExistingCenterInfoWithWebPage(domain, webPageKey, loadedScriptsKey, loadedScriptMetricoolKey, loadedScriptsGoogleKey, centerInfo, observable)
                });
            }
        });
    }

    private handleExistingCenterInfoWithWebPage(
        domain: string,
        webPageKey: string,
        loadedScriptsKey: string,
        loadedScriptMetricoolKey: string,
        loadedScriptsGoogleKey: string,
        centerInfo: CenterInfo,
        observable: Subscriber<boolean>
    ): void {
        this.cacheService
            .get(webPageKey, this.centerService.getWebPage(domain), 3600)
            .subscribe((data) => this.handleExistingCenterInfo(data, loadedScriptsKey, loadedScriptMetricoolKey, loadedScriptsGoogleKey, centerInfo, observable));
    }

    private handleInfoAndReload(
        data: WebPage,
        domain: string,
        loadedScriptsKey: string,
        loadedScriptMetricoolKey: string,
        loadedScriptsGoogleKey: string,
        centerInfo: CenterInfo,
        observable: Subscriber<boolean>,
        info: any
    ): void {
        if (info.showMeridian) {
            info.hourFormat = this.DEFAULT_HOUR_FORMAT_12;
        } else {
            info.hourFormat = this.DEFAULT_HOUR_FORMAT_24;
        }

        const centerInfoCurrent = new CenterInfo().build(info);
        centerInfoCurrent.domain = new Domain().build({ url: domain, type: data.domainType });
        if (centerInfoCurrent.isActiveTranslator) {
            document.documentElement.style.setProperty('--show-translations', 'block');
        } else {
            document.documentElement.style.setProperty('--show-translations', 'none');
        }

        this.internationalizationService.setLanguage(centerInfoCurrent.codeLanguage);
        this.internationalizationService.setLocale(centerInfoCurrent.locale);
        this.internationalizationService.setDateFormat(centerInfoCurrent.dateFormat);
        this.internationalizationService.setHourFormat(centerInfoCurrent.hourFormat);
        this.internationalizationService.setFirstDayOfWeek(centerInfoCurrent.firstWeekDay % 7);
        this.internationalizationService.setCountryCode(centerInfoCurrent.codeCountry);
        this.internationalizationService.setCurrency(centerInfoCurrent.currencyCode);
        this.internationalizationService.setDigits(centerInfoCurrent.codeCountry);

        this.internationalizationService.importLocale();
        this.translateService.use(centerInfoCurrent.codeLanguage);

        this.sessionService.setCenterInfo(centerInfoCurrent);

        // [TD-18571] Vulture Fitness & Box (9694) usa dominio propio pero requiere el banner de Cookiebot de Trainingym
        if (data.domainType === WebPageDomainType.domain && data.idModule !== 9694) {
            if (data.scripts) {
                this.cacheService.get(loadedScriptsKey, this.scriptService.loadScriptsHeader(data.scripts), 3600).subscribe();
            }
        } else {
            this.cacheService.get(loadedScriptsKey, this.scriptService.loadScriptsHeader(CookiebotHelper.getCookieBotUCScripts()), 3600).subscribe();
            this.loadGoogleTagScripts(data.idModule, loadedScriptsGoogleKey);
        }

        if (centerInfoCurrent.hash !== null) {
            this.cacheService.get(loadedScriptMetricoolKey, this.scriptService.loadScriptsHeader(MetricoolHelper.getMetricoolScripts(centerInfoCurrent.hash)), 3600).subscribe();
        }

        this.appLoadedService.unload();
        setTimeout(() => {
            if (centerInfo.domain.url.toUpperCase() !== domain.toUpperCase()) {
                this.sessionService.setDomain(domain);
                this.sessionService.logout();
            }

            this.appLoadedService.load();
            location.reload();
            observable.next(true);
        }, 300);
    }

    private handleExistingCenterInfo(data: WebPage, loadedScriptsKey: string, loadedScriptMetricoolKey: string, loadedScriptsGoogleKey: string, centerInfo: CenterInfo, observable: Subscriber<boolean>): void {
        // [TD-18571] Vulture Fitness & Box (9694) usa dominio propio pero requiere el banner de Cookiebot de Trainingym
        if (data.domainType === WebPageDomainType.domain && data.idModule !== 9694) {
            if (data.scripts) {
                this.cacheService.get(loadedScriptsKey, this.scriptService.loadScriptsHeader(data.scripts), 3600).subscribe();
            }
        } else {
            this.cacheService.get(loadedScriptsKey, this.scriptService.loadScriptsHeader(CookiebotHelper.getCookieBotUCScripts()), 3600).subscribe();
            this.loadGoogleTagScripts(data.idModule, loadedScriptsGoogleKey);
        }
        if (centerInfo.hash !== null) {
            this.cacheService.get(loadedScriptMetricoolKey, this.scriptService.loadScriptsHeader(MetricoolHelper.getMetricoolScripts(centerInfo.hash)), 3600).subscribe();
        }
        const centerInfoCurrent = this.sessionService.getCenterInfo();
        if (centerInfoCurrent.isActiveTranslator) {
            document.documentElement.style.setProperty('--show-translations', 'block');
        } else {
            document.documentElement.style.setProperty('--show-translations', 'none');
        }
        this.internationalizationService.setLanguage(centerInfoCurrent.codeLanguage);
        this.internationalizationService.setLocale(centerInfoCurrent.locale);
        this.internationalizationService.setDateFormat(centerInfoCurrent.dateFormat);
        this.internationalizationService.setHourFormat(centerInfoCurrent.hourFormat);
        this.internationalizationService.setFirstDayOfWeek(centerInfoCurrent.firstWeekDay % 7);
        this.internationalizationService.setCountryCode(centerInfoCurrent.codeCountry);
        this.internationalizationService.setCurrency(centerInfoCurrent.currencyCode);
        this.internationalizationService.setDigits(centerInfoCurrent.codeCountry);
        this.internationalizationService.importLocale();
        this.translateService.use(centerInfoCurrent.codeLanguage);

        this.sessionService.setCenterInfo(centerInfoCurrent);

        this.appLoadedService.load();

        observable.next(true);
    }

    private loadGoogleTagScripts(idCenter: number, loadedScriptsGoogleKey: string): void {
        if (!GoogleTagHelper.isGoogleTagCenter(idCenter)) {
            return;
        }

        this.cacheService.get(loadedScriptsGoogleKey, this.scriptService.loadScriptsHeader(GoogleTagHelper.getHeaderScripts(idCenter)), 3600).subscribe();
        this.scriptService.loadNoScriptBody(GoogleTagHelper.getNoScript(idCenter)).subscribe();
    }
}
